import { MODELS_CLASS_NAMES, RESEARCH_MERGE_ERROS } from '@/config/research';
import unique from 'lodash/uniq';

function dataIdsReducer(dataIds, { keyImages, seriesImages, className, id, segmentations }) {
  let attachmentIds = [];
  const segmentationIds = segmentations?.map(({ id }) => id) ?? [];
  const keyImagesIds = keyImages?.map(({ id }) => id) ?? [];
  const seriesImagesIds = seriesImages?.map(({ id }) => id) ?? [];

  if (className === MODELS_CLASS_NAMES.RESEARCH_ATTACHMENT) {
    attachmentIds.push(id);
  }

  return unique([...dataIds, ...keyImagesIds, ...seriesImagesIds, ...attachmentIds, ...segmentationIds]);
}

export const RepositoryUpload = {
  namespaced: true,

  state: {
    linkingUploadToInstanceByModule: {},
    instanceDataIds: {},
    loadedData: false,
    loading: false,
    instances: {},
    upload: null,
    segmentationsPerStudyInstance: [],
    isExternalSegmentationsButtonClicked: false,
    isStudiesButtonClicked: false,
    isStudiesInputButtonClicked: false,
    selectedStudies: [],
    selectedSeries: {},
    hasInteractedWithSeries: false,
    mergeError: null,
    isMergingSeries: false,
  },

  getters: {
    isUploadLoading: (state) => state.loading,

    isDataLoaded: (state) => state.loadedData,

    isUploadInstanceLoaded: (state) => (uuid) => {
      return Object.keys(state.instances).includes(uuid);
    },

    getInstanceForModuleAndSubject:
      ({ instances }) =>
      (module, subject) => {
        return instances?.[module]?.[subject] ?? null;
      },

    getInstanceDataIds:
      ({ instanceDataIds }) =>
        (instanceId) => {
          return (instanceDataIds[instanceId] ?? [])?.reduce(dataIdsReducer, []);
        },

    isUploadInstanceDataLoaded: (state) => (instanceId) => {
      return state.instanceDataIds?.[instanceId] !== undefined;
    },

    loadUploadData: (state) => (instanceId) => {
      return state.instanceDataIds?.[instanceId] ?? [];
    },

    getLinkingUploadToInstance: (state) => (module) => state.linkingUploadToInstanceByModule[module],
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setDataLoaded(state, bool) {
      state.loadedData = bool;
    },

    setUpload(state, upload) {
      state.upload = upload;
    },

    setUploadInstanceForModuleAndSubject(state, { module, subject, instance }) {
      let moduleInstances = state.instances[module] ?? {};

      moduleInstances = { ...moduleInstances, [subject]: instance };

      state.instances = { ...state.instances, [module]: moduleInstances };
    },

    setSegmentationsPerStudyInstance(state, payload) {
      state.segmentationsPerStudyInstance = payload;
    },

    setUploadData(state, { instance, data, isUpload }) {
      if (data?.length) {
        data.forEach(function (item) {
          Vue.set(item, '_showDetails', false);
        });
      } else if (data) {
        Vue.set(data, '_showDetails', false);
      } else {
        return;
      }

      const a = state.instanceDataIds[instance] ?? [];

      Vue.set(state.instanceDataIds, instance, isUpload ? [...a, ...data] : data);
    },

    toggleRowDetails(state, { item, instance, details }) {
      const itemToToggle = state.instanceDataIds[instance].find((data) => data.id === item.id && details !== null);
      Vue.set(itemToToggle, 'details', details);
      Vue.set(itemToToggle, '_showDetails', !itemToToggle._showDetails);
    },

    removeUploadData(state, { instance, data }) {
      const currentData = state.instanceDataIds ?? {};
      const filteredItems = (currentData[instance] ?? []).filter((item) => item.id !== data);

      state.instanceDataIds = { ...currentData, [instance]: filteredItems };
    },

    setLinkingUploadToInstanceByModule(state, { module, value }) {
      Vue.set(state.linkingUploadToInstanceByModule, module, value);
    },

    setStatusOfExternalSegmentationsButtonClicked(state, booleanPayload) {
      state.isExternalSegmentationsButtonClicked = booleanPayload;
    },

    setStatusOfStudiesButtonClickedStatus(state, booleanPayload) {
      state.isStudiesButtonClicked = booleanPayload;
    },
    setStatusOfStudiesInputButtonClickedStatus(state, booleanPayload) {
      state.isStudiesInputButtonClicked = booleanPayload;
    },

    setSelectedStudies(state, payload) {
      state.selectedStudies = payload;
    },

    setSelectedSeries(state, { studyId, series, reset = false }) {
      if (reset) {
        state.selectedSeries = {};
        return;
      }
      Vue.set(state.selectedSeries, studyId, series);
    },

    setHasInteractedWithSeries(state, payload) {
      state.hasInteractedWithSeries = payload;
    },

    clearSeriesAndStudies(state) {
      state.selectedStudies = [];
      state.selectedSeries = {};
    },

    setMergeError(state, payload) {
      state.mergeError = payload;
    },

    setIsMergingSeries(state, payload) {
      state.isMergingSeries = payload;
    },
  },

  actions: {
    async loadRepositoryUpload({ commit }, { repository }) {
      if (!repository) return;

      commit('setLoading', true);
      let upload = null;

      try {
        const response = await this._vm.$api('repositories.upload.store', {
          version: '1.0',
          repository,
        });

        upload = response.data.data;
        commit('setUpload', upload);
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async loadUploadInstance(
      { commit },
      { repository, module, subject, project, event, pipeline, version, run_version }
    ) {
      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.upload.instance', {
          version: '1.0',
          repository,
          project,
          pipeline,
          event,
          module,
          subject,
          v: version,
          run_version,
        });

        commit('setUploadInstanceForModuleAndSubject', {
          instance: response.data.data,
          subject,
          module,
        });
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async linkUploadToInstance({ commit, dispatch }, { repository, project, pipeline, instance, upload }) {
      commit('setLinkingUploadToInstanceByModule', { module, value: true });
      try {
        const response = await this._vm.$api(
          'research.upload.instance.link',
          {
            version: '1.0',
            repository,
            pipeline,
            instance,
            project,
          },
          upload
        );

        const loadedData = { ...response.data.upload, ...(upload.data.preloaded ? { preloaded: true } : {}) };
        dispatch('getInstanceData', { instance });

        commit('setUploadData', { instance, data: [loadedData], isUpload: true });
      } catch (err) {
        console.error(err);
      }
      commit('setLinkingUploadToInstanceByModule', { module, value: false });
    },

    async getInstanceData({ commit }, { instance }) {
      commit('setDataLoaded', false);
      try {
        const response = await this._vm.$api('research.upload.instance.data', {
          version: '1.0',
          instance,
        });

        const responseUploadsWithSegmentationsMerged = response?.data?.uploads?.map((upload) => {
          if (upload.segmentations && upload.segmentations.length > 0) {
            return { ...upload, segmentations: [upload.segmentations[upload.segmentations.length - 1]] };
          } else {
            return upload;
          }
        });

        commit('setUploadData', { instance, data: responseUploadsWithSegmentationsMerged });
        commit(
          'setSegmentationsPerStudyInstance',
          responseUploadsWithSegmentationsMerged?.flatMap((item) => item.segmentations)
        );
        commit;
      } catch (err) {
        console.error(err);
      } finally {
        commit('setDataLoaded', true);
      }
    },

    async deleteUpload({ commit }, { repository, instance, uploadInstance, data }) {
      commit('removeUploadData', { instance: instance, data });

      try {
        await this._vm.$api('repositories.upload.destroy', {
          upload: uploadInstance,
          version: '1.0',
          repository,
        });
      } catch (err) {
        console.error(err);
      }
    },

    externalSegmentationsButtonClickedStatus({ commit }, booleanPayload) {
      commit('setStatusOfExternalSegmentationsButtonClicked', booleanPayload);
    },

    studiesButtonClickedStatus({ commit }, booleanPayload) {
      commit('setStatusOfStudiesButtonClickedStatus', booleanPayload);
    },
    studiesInputButtonClickedStatus({ commit }, booleanPayload) {
      commit('setStatusOfStudiesInputButtonClickedStatus', booleanPayload);
    },

    async mergeSeries({ commit }, { repository, project, seriesIds, projectId }) {
      commit('setLoading', true);
      commit('setMergeError', null);

      try {
        const response = await this._vm.$api(
          'research.dicom.studies.merge',
          {
            repository,
            project,
          },
          { seriesIds, projectId }
        );

        commit('setLoading', false);

        return response.data.data;
      } catch (e) {
        if (e?.message?.includes(RESEARCH_MERGE_ERROS.NOT_SAME_PATIENT)) {
          commit('setMergeError', e.message);
        }
        commit('setLoading', false);
        commit('setMergeError', e.message);
        console.error(e);
      }
    },
  },
};
